import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { Icon, type IconName } from './icons';
import type { ButtonVariant } from './buttons';

/**
 * Button with a small popup menu of links/actions (e.g. "Mehr" on the house view). Menu pattern: aria-haspopup,
 * arrow keys move between items, Escape and outside clicks close it, focus returns to the button.
 * `danger` marks destructive items (red text, set apart below a line at the end); `hint` adds a muted second line (e.g. why
 * something is not possible); `section` groups items under a small heading (consecutive items with the same section).
 */
export type MenuItem = { key: string; label: ReactNode; to?: string; onClick?: () => void; icon?: IconName; hidden?: boolean; danger?: boolean; hint?: ReactNode; section?: string };

function useMenuKeys(pop: RefObject<HTMLElement | null>, close: () => void, setOpen: (o: boolean) => void) {
  const focusItem = (i: number) => {
    const els = pop.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (els?.length) els[(i + els.length) % els.length]!.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    const els = [...(pop.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const cur = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(cur + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(cur - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusItem(0); }
    else if (e.key === 'End') { e.preventDefault(); focusItem(els.length - 1); }
    else if (e.key === 'Tab') setOpen(false);
  };
  return { focusItem, onKey };
}

function MenuEntry({ i, close }: { i: MenuItem; close: (refocus?: boolean) => void }) {
  const content = (
    <>
      {i.icon && <Icon name={i.icon} size={16} strokeWidth={1.8} />}
      <span className="menu-label">
        {i.label}
        {i.hint && <small>{i.hint}</small>}
      </span>
    </>
  );
  return i.to ? (
    <Link to={i.to} role="menuitem" tabIndex={-1} className={i.danger ? 'danger' : undefined} onClick={() => close(false)}>{content}</Link>
  ) : (
    <button type="button" role="menuitem" tabIndex={-1} className={i.danger ? 'danger' : undefined} onClick={() => { close(); i.onClick?.(); }}>{content}</button>
  );
}

/** Consecutive items with the same `section` form one group; destructive items always form the last group. */
function groupItems(items: MenuItem[]): { key: string; section?: string; danger: boolean; items: MenuItem[] }[] {
  const ordered = [...items.filter((i) => !i.danger), ...items.filter((i) => i.danger)];
  const groups: { key: string; section?: string; danger: boolean; items: MenuItem[] }[] = [];
  for (const i of ordered) {
    const last = groups[groups.length - 1];
    const danger = !!i.danger;
    if (last && last.danger === danger && (danger || last.section === i.section)) last.items.push(i);
    else groups.push({ key: i.key, section: danger ? undefined : i.section, danger, items: [i] });
  }
  return groups;
}

function MenuItems({ items, close }: { items: MenuItem[]; close: (refocus?: boolean) => void }) {
  const groups = groupItems(items);
  // no sections and nothing destructive: a plain list as before
  if (groups.length === 1 && !groups[0]!.section && !groups[0]!.danger) return <>{items.map((i) => <MenuEntry key={i.key} i={i} close={close} />)}</>;
  return (
    <>
      {groups.map((g, n) => (
        <div key={g.key} role="group" aria-label={g.section} className={`menu-group${n > 0 ? ' sep' : ''}${g.danger ? ' danger-group' : ''}`}>
          {g.section && <span className="menu-section" aria-hidden>{g.section}</span>}
          {g.items.map((i) => <MenuEntry key={i.key} i={i} close={close} />)}
        </div>
      ))}
    </>
  );
}

export function MenuButton({ label, items, icon = 'chevronDown', variant = 'secondary', align = 'end', children }: { label: string; items: MenuItem[]; icon?: IconName; variant?: ButtonVariant; align?: 'start' | 'end'; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const visible = items.filter((i) => !i.hidden);
  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const { focusItem, onKey } = useMenuKeys(wrap, close, setOpen);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    requestAnimationFrame(() => focusItem(0));
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  return (
    <div className="menu-wrap" ref={wrap} onKeyDown={open ? onKey : undefined}>
      <button ref={btn} type="button" className={`btn ${variant}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}>
        {children ?? label}
        <Icon name={icon} size={16} strokeWidth={1.8} className={`menu-caret ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className={`menu-pop ${align}`} role="menu" id={id} aria-label={label}>
          <MenuItems items={visible} close={close} />
        </div>
      )}
    </div>
  );
}

/**
 * Row actions ("⋯") for list and table rows: an always visible icon button (never hover-only) with a menu of
 * actions for that row (Bearbeiten, Löschen …). The popup is placed in a layer above the page, so table scroll
 * containers never cut it off; it flips above the button when there is no room below.
 */
export function RowMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();
  const visible = items.filter((i) => !i.hidden);
  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const { focusItem, onKey } = useMenuKeys(pop, close, setOpen);
  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const place = () => {
      const r = btn.current!.getBoundingClientRect();
      const h = pop.current?.offsetHeight ?? 0;
      const w = pop.current?.offsetWidth ?? 240;
      const up = r.bottom + 6 + h > window.innerHeight && r.top - 6 - h > 0;
      const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
      setPos({ top: up ? r.top - 6 - h : r.bottom + 6, left, up });
    };
    place();
    const onDown = (e: MouseEvent) => { if (!pop.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false); };
    const onMove = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    requestAnimationFrame(() => { place(); focusItem(0); });
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);
  if (!visible.length) return null;
  return (
    <>
      <button ref={btn} type="button" className="btn ghost icon row-menu-btn" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o); }}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}>
        <Icon name="more" size={20} strokeWidth={2.2} />
      </button>
      {open && createPortal(
        <div ref={pop} className="menu-pop row-menu-pop" role="menu" id={id} aria-label={label} onKeyDown={onKey} onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}>
          <MenuItems items={visible} close={close} />
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * Quick-action bar of a page header: the few actions that fit the current state are visible buttons (icon + short
 * label, `primary` filled, the others secondary), everything else stays in "Mehr", grouped by `section`, destructive
 * ones set apart at the bottom. One list of actions, `quick` picks (in order, at most `max`) which are visible; an
 * action is never in both places. On phone width the visible actions become a horizontally scrollable chip row,
 * "Mehr" stays at the end outside the scroll area (its menu is never cut off).
 */
export function ActionBar({ label, moreLabel, items, quick, primary, max = 4 }: {
  /** Accessible name of the group, e.g. "Schnellaktionen". */
  label: string;
  moreLabel: string;
  items: MenuItem[];
  /** Keys of the actions shown as buttons, most important first. */
  quick: string[];
  /** Key of the one filled action (default: none). */
  primary?: string;
  max?: number;
}) {
  const visible = items.filter((i) => !i.hidden);
  const shown = quick.map((k) => visible.find((i) => i.key === k)).filter((i): i is MenuItem => !!i && !i.danger).slice(0, max);
  const keys = new Set(shown.map((i) => i.key));
  const rest = visible.filter((i) => !keys.has(i.key));
  if (!shown.length && !rest.length) return null;
  return (
    <div className="action-bar" role="group" aria-label={label}>
      {shown.length > 0 && (
        <div className="ab-scroll">
          {shown.map((i) => {
            const cls = `btn ${i.key === primary ? 'primary' : 'secondary'} ab-action`;
            const body = <>{i.icon && <Icon name={i.icon} size={18} strokeWidth={1.8} />}<span>{i.label}</span></>;
            return i.to ? <Link key={i.key} to={i.to} className={cls}>{body}</Link> : <button key={i.key} type="button" className={cls} onClick={i.onClick}>{body}</button>;
          })}
        </div>
      )}
      {rest.length > 0 && <MenuButton label={moreLabel} items={rest} />}
    </div>
  );
}
